import {
  mkdir,
  readdir,
  readFile,
  rename,
  rm,
  rmdir,
  unlink,
  writeFile,
} from 'node:fs/promises';
import path from 'node:path';
import { setTimeout as sleep } from 'node:timers/promises';
import {
  type CreateTaskInput,
  cloneTask,
  sortTasks,
  type TaskListState,
  type TaskListStore,
  type TaskRecord,
  TaskRecordSchema,
} from './types';

const HIGH_WATERMARK_FILE = '.highwatermark';
const LOCK_DIR = '.lock';

type StoredTaskList = {
  tasks: TaskRecord[];
  highWatermark: number;
};

export class InMemoryTaskListStore implements TaskListStore {
  readonly #lists = new Map<string, StoredTaskList>();

  async transaction<T>(
    taskListId: string,
    fn: (state: TaskListState) => T | Promise<T>,
  ): Promise<T> {
    const existing = this.#lists.get(taskListId) ?? {
      tasks: [],
      highWatermark: 0,
    };
    const state = new MutableTaskListState(
      existing.tasks,
      existing.highWatermark,
    );
    const result = await fn(state);
    if (state.dirty) {
      this.#lists.set(taskListId, state.snapshot());
    }
    return result;
  }
}

export class FileTaskListStore implements TaskListStore {
  constructor(
    private readonly rootDir = path.join(process.cwd(), '.agents', 'tasks'),
  ) {}

  async transaction<T>(
    taskListId: string,
    fn: (state: TaskListState) => T | Promise<T>,
  ): Promise<T> {
    const listDir = this.#listDir(taskListId);
    await mkdir(listDir, { recursive: true });

    return await withFileLock(listDir, async () => {
      const stored = await readTaskList(listDir);
      const state = new MutableTaskListState(
        stored.tasks,
        stored.highWatermark,
      );
      const result = await fn(state);
      if (state.dirty) {
        await writeTaskList(listDir, state.snapshot());
      }
      return result;
    });
  }

  #listDir(taskListId: string): string {
    return path.join(this.rootDir, encodeURIComponent(taskListId));
  }
}

class MutableTaskListState implements TaskListState {
  readonly #tasks = new Map<string, TaskRecord>();
  #highWatermark: number;
  dirty = false;

  constructor(tasks: TaskRecord[], highWatermark: number) {
    for (const task of tasks) {
      this.#tasks.set(task.id, cloneTask(task));
    }
    this.#highWatermark = Math.max(
      highWatermark,
      ...tasks.map((task) => numericTaskId(task.id) ?? 0),
      0,
    );
  }

  list(): TaskRecord[] {
    return sortTasks([...this.#tasks.values()]).map((task) => cloneTask(task));
  }

  get(id: string): TaskRecord | undefined {
    const task = this.#tasks.get(id);
    return task ? cloneTask(task) : undefined;
  }

  create(input: CreateTaskInput): TaskRecord {
    this.#highWatermark++;
    const task = TaskRecordSchema.parse({
      id: String(this.#highWatermark),
      subject: input.subject,
      description: input.description,
      ...(input.activeForm !== undefined
        ? { activeForm: input.activeForm }
        : {}),
      status: 'pending',
      blocks: [],
      blockedBy: [],
      ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
    });
    this.#tasks.set(task.id, cloneTask(task));
    this.dirty = true;
    return cloneTask(task);
  }

  save(task: TaskRecord): TaskRecord {
    const parsed = TaskRecordSchema.parse(task);
    this.#tasks.set(parsed.id, cloneTask(parsed));
    this.dirty = true;
    return cloneTask(parsed);
  }

  delete(id: string): boolean {
    const didDelete = this.#tasks.delete(id);
    if (didDelete) {
      this.dirty = true;
    }
    return didDelete;
  }

  snapshot(): StoredTaskList {
    return {
      tasks: sortTasks([...this.#tasks.values()]).map((task) =>
        cloneTask(task),
      ),
      highWatermark: this.#highWatermark,
    };
  }
}

async function readTaskList(listDir: string): Promise<StoredTaskList> {
  const entries = await readdir(listDir).catch((error) => {
    if (isNodeError(error, 'ENOENT')) {
      return [];
    }
    throw error;
  });
  const tasks: TaskRecord[] = [];

  for (const entry of entries) {
    if (!entry.endsWith('.json')) {
      continue;
    }
    const contents = await readFile(path.join(listDir, entry), 'utf8');
    tasks.push(TaskRecordSchema.parse(JSON.parse(contents)));
  }

  const highWatermark = await readHighWatermark(listDir, tasks);
  return {
    tasks: sortTasks(tasks),
    highWatermark,
  };
}

async function writeTaskList(
  listDir: string,
  stored: StoredTaskList,
): Promise<void> {
  await mkdir(listDir, { recursive: true });
  const taskIds = new Set(stored.tasks.map((task) => task.id));
  const entries = await readdir(listDir).catch((error) => {
    if (isNodeError(error, 'ENOENT')) {
      return [];
    }
    throw error;
  });

  for (const entry of entries) {
    if (!entry.endsWith('.json')) {
      continue;
    }
    const id = decodeURIComponent(entry.slice(0, -'.json'.length));
    if (!taskIds.has(id)) {
      await unlink(path.join(listDir, entry)).catch((error) => {
        if (!isNodeError(error, 'ENOENT')) {
          throw error;
        }
      });
    }
  }

  for (const task of stored.tasks) {
    await writeJsonAtomic(
      taskPath(listDir, task.id),
      TaskRecordSchema.parse(task),
    );
  }
  await writeFile(
    path.join(listDir, HIGH_WATERMARK_FILE),
    `${stored.highWatermark}\n`,
    'utf8',
  );
}

async function readHighWatermark(
  listDir: string,
  tasks: TaskRecord[],
): Promise<number> {
  const highWatermarkPath = path.join(listDir, HIGH_WATERMARK_FILE);
  const fallback = Math.max(
    ...tasks.map((task) => numericTaskId(task.id) ?? 0),
    0,
  );
  try {
    const contents = await readFile(highWatermarkPath, 'utf8');
    const parsed = Number.parseInt(contents.trim(), 10);
    return Number.isInteger(parsed) ? Math.max(parsed, fallback) : fallback;
  } catch (error) {
    if (isNodeError(error, 'ENOENT')) {
      return fallback;
    }
    throw error;
  }
}

async function writeJsonAtomic(
  filePath: string,
  value: unknown,
): Promise<void> {
  const tempPath = `${filePath}.${process.pid}.${Date.now()}.tmp`;
  await writeFile(tempPath, `${JSON.stringify(value, null, 2)}\n`, 'utf8');
  await rename(tempPath, filePath);
}

function taskPath(listDir: string, taskId: string): string {
  return path.join(listDir, `${encodeURIComponent(taskId)}.json`);
}

function numericTaskId(id: string): number | undefined {
  const parsed = Number(id);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

async function withFileLock<T>(
  listDir: string,
  fn: () => Promise<T>,
): Promise<T> {
  const lockDir = path.join(listDir, LOCK_DIR);
  await acquireLock(lockDir);
  try {
    return await fn();
  } finally {
    await rmdir(lockDir).catch(async (error) => {
      if (!isNodeError(error, 'ENOENT')) {
        await rm(lockDir, { recursive: true, force: true });
      }
    });
  }
}

async function acquireLock(lockDir: string): Promise<void> {
  const startedAt = Date.now();
  while (true) {
    try {
      await mkdir(lockDir);
      return;
    } catch (error) {
      if (!isNodeError(error, 'EEXIST')) {
        throw error;
      }
      if (Date.now() - startedAt > 5_000) {
        throw new Error(`Timed out waiting for task list lock: ${lockDir}`);
      }
      await sleep(25);
    }
  }
}

function isNodeError(error: unknown, code: string): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error as { code?: unknown }).code === code
  );
}
